import debounce from 'lodash/debounce';
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';

import hooks from '../../../../shared/hooks';
import BaseTooltip, { TooltipDirection, TooltipPosition } from './BaseTooltip';

const getTooltipPosition = (tooltip: HTMLElement, parent: HTMLElement, direction: TooltipDirection, offset: number): TooltipPosition => {
    const ttRectangle = tooltip.getBoundingClientRect();
    const parentRectangle = parent.getBoundingClientRect();
    const { scrollX, scrollY } = window;

    let left = 0;
    let top = 0;
    let shiftArrowLeft = false;
    let shiftArrowRight = false;

    switch (direction) {
        case TooltipDirection.top:
        case TooltipDirection.bottom:
            left = scrollX + parentRectangle.left + (parentRectangle.width / 2) - (ttRectangle.width / 2);

            if (left < 0) {
                left = scrollX + parentRectangle.left;
                shiftArrowLeft = true;
            } else {
                if (left+ttRectangle.width>document.documentElement.clientWidth) {
                    left = scrollX + parentRectangle.left + parentRectangle.width - ttRectangle.width;
                    shiftArrowRight = true;
                }
            }

            break;

        case TooltipDirection.bottomRight:
            shiftArrowRight = true;
            left = scrollX + parentRectangle.left + parentRectangle.width - ttRectangle.width + 2;
            break;

        case TooltipDirection.left:
            left = scrollX + parentRectangle.left - ttRectangle.width - offset;
            break;

        case TooltipDirection.right:
            left = scrollX + parentRectangle.left + parentRectangle.width + offset;
            break;

        case TooltipDirection.overlay:
            left = scrollX + parentRectangle.left - 4;
            break;
    }

    switch (direction) {
        case TooltipDirection.top:
            top = scrollY + parentRectangle.top - ttRectangle.height - offset;
            break;

        case TooltipDirection.bottom:
            top = scrollY + parentRectangle.top + parentRectangle.height + offset;
            break;

        case TooltipDirection.bottomRight:
            top = scrollY + parentRectangle.top + parentRectangle.height + offset + 2;
            break;

        case TooltipDirection.right:
        case TooltipDirection.left:
            top = scrollY + parentRectangle.top + (parentRectangle.height / 2) - (ttRectangle.height / 2);
            break;

        case TooltipDirection.overlay:
            top = scrollY + parentRectangle.top + 2;
            break;
    }

    return {
        left,
        top,
        shiftArrowLeft,
        shiftArrowRight
    };
};

export interface TooltipProps {
    className?: string;
    direction: TooltipDirection;
    maxWidth?: string;
    offset?: number;
    parent: HTMLElement | null;
    visible: boolean;
}

const Tooltip: React.FC<PropsWithChildren<TooltipProps>> = ({
    children,
    className = '',
    direction,
    maxWidth,
    offset = 4,
    parent,
    visible
}) => {
    const [ coords, setCoords ] = useState<TooltipPosition | null>(null);
    const tooltipRef = useRef() as React.MutableRefObject<HTMLDivElement>;

    const updateCoords = () => {
        if (parent && tooltipRef.current) {
            const coords = getTooltipPosition(tooltipRef.current, parent, direction, offset);

            setCoords(coords);
        }
    };

    hooks.interactions.useScrollObserver(parent, updateCoords);

    const debouncedUpdateCoords = debounce( updateCoords, 50);

    useEffect(() => {
        if (parent) {
            window.addEventListener('resize', debouncedUpdateCoords);

            return () => {
                window.removeEventListener('resize', debouncedUpdateCoords);
            };
        }
    }, [ parent ]);

    useEffect(() => {
        updateCoords();
    }, [ visible ]);

    return (
        <BaseTooltip className={ className }
            direction={ direction }
            maxWidth={ maxWidth }
            onLayout={ updateCoords }
            position={ coords }
            getTooltipRef={ tooltipRef }
            visible={ visible }
        >
            { children }
        </BaseTooltip>
    );
};

export default Tooltip;
